import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { BOOKING_URL, HERO_IMAGE } from "@/lib/constants";
import ParticleField from "@/components/ParticleField";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const words = ["IEEPA", "Tariff", "Refund", "Recovery"];

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.35 },
  },
};
const word = {
  hidden: { y: "110%", opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.9, ease: [0.2, 0.8, 0.2, 1] },
  },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden section-dark"
    >
      {/* Background image with parallax */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={HERO_IMAGE}
          alt="Port cranes at sunset"
          className="w-full h-full object-cover object-center"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#040b16]/95 via-[#040b16]/80 to-[#040b16]/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#040b16] via-transparent to-[#040b16]/30" />
      </motion.div>

      {/* Grid lines */}
      <div className="absolute inset-0 grid-lines opacity-40" />

      {/* Ambient gold glow */}
      <motion.div
        aria-hidden
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full gold-glow blur-3xl"
        animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.7, 0.5] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full gold-glow blur-3xl"
        animate={{ scale: [1.05, 1, 1.05], opacity: [0.35, 0.55, 0.35] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Particles */}
      <ParticleField />

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-40 pb-24 min-h-screen flex flex-col justify-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="h-px w-14 bg-[#F5B800]" />
          <span
            data-testid="hero-overline"
            className="font-mono uppercase tracking-[0.24em] text-[11px] sm:text-xs text-[#F5B800]"
          >
            Supreme Court Ruling · Feb 20, 2026
          </span>
        </motion.div>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-serif text-white text-[clamp(2.75rem,7.5vw,6.5rem)] leading-[0.95] tracking-tight max-w-5xl"
          data-testid="hero-headline"
        >
          {words.map((w, i) => (
            <span
              key={w}
              className="inline-block overflow-hidden align-baseline mr-4 sm:mr-6"
            >
              <motion.span
                variants={word}
                className={`inline-block ${
                  i === 3 ? "italic text-shimmer" : ""
                }`}
              >
                {w}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.9 }}
          className="mt-10 text-lg sm:text-2xl text-white/85 max-w-2xl leading-relaxed font-light"
          data-testid="hero-subheadline"
        >
          You paid unlawful tariffs in 2025.{" "}
          <span className="text-white font-medium">We get your money back.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.75, duration: 0.8 }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-primary-cta"
            className="btn-magnetic group inline-flex items-center gap-3 bg-[#F5B800] hover:bg-[#DDA600] text-[#0A1936] font-semibold text-base px-8 py-5 rounded-sm shadow-[0_10px_40px_-10px_rgba(245,184,0,0.6)] transition-colors"
          >
            Book Free 15-min Assessment
            <ArrowUpRight
              className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
              size={20}
            />
          </a>
          <a
            href="#situation"
            data-testid="hero-secondary-cta"
            className="group inline-flex items-center gap-2 text-white/90 hover:text-[#F5B800] font-medium text-sm tracking-wide transition-colors"
          >
            <span className="border-b border-white/40 group-hover:border-[#F5B800] pb-1 transition-colors">
              See if you qualify
            </span>
            <ArrowDown
              size={16}
              className="transition-transform group-hover:translate-y-1"
            />
          </a>
        </motion.div>

        {/* Bottom-left meta */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="mt-20 flex flex-wrap items-center gap-x-10 gap-y-4 text-white/60 text-xs font-mono uppercase tracking-[0.2em]"
        >
          <span>Zero upfront cost</span>
          <span className="hidden sm:inline">·</span>
          <span>20% only on recovery</span>
          <span className="hidden sm:inline">·</span>
          <span>Filing windows open now</span>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.3, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-2 text-white/50"
        >
          <span className="font-mono uppercase text-[10px] tracking-[0.3em]">
            Scroll
          </span>
          <div className="w-px h-10 bg-gradient-to-b from-white/50 to-transparent" />
        </motion.div>
      </motion.div>
    </section>
  );
}
